"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import axios from "axios";
import { Star, X, Loader2, Image as ImageIcon, MessageSquare, CheckCircle2 } from "lucide-react";
import { API_BASE_URL, getImageUrl } from '@/app/lib/api';
import type { Product } from "./ProductClientView";

interface Review {
  id?: string | number;
  note?: number;
  rating?: number;
  nom?: string;
  name?: string;
  commentaire?: string;
  description?: string;
  texte?: string;
  createdAt?: string;
  created_at?: string;
}

interface Props {
  product?: Product;
  productId?: string;
  reviews?: Review[];
  totalReviews?: number;
}

export default function ReviewsSection({ product, productId, reviews: propReviews, totalReviews: propTotalReviews }: Props) {
  const [shortReviews, setShortReviews] = useState<{ reviews: Review[]; count: number } | null>(null);
  const [loadingShortReviews, setLoadingShortReviews] = useState(false);
  const [isReviewModalOpen, setReviewModalOpen] = useState(false);
  const [formName, setFormName] = useState("");
  const [formComment, setFormComment] = useState("");
  const [formImage, setFormImage] = useState<File | null>(null);
  const [formRating, setFormRating] = useState(0);
  const [formHoverRating, setFormHoverRating] = useState(0);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const resolvedProductId = product?.id ?? productId;
  const rawReviews = Array.isArray(propReviews) ? propReviews : (product as any)?.reviews ?? [];
  const reviews: Review[] = Array.isArray(rawReviews) ? (rawReviews as Review[]) : [];

  const currentReviews = shortReviews?.reviews || reviews;
  const currentCount = shortReviews?.count || propTotalReviews || reviews.length;

  const averageRating = currentCount > 0
    ? currentReviews.reduce((acc, r) => acc + Number(r.note || r.rating || 0), 0) / currentReviews.length
    : 0;

  const ratingLabels = ["", "Mauvais", "Passable", "Bien", "Excellent", "Parfait !"];

  useEffect(() => {
    if (!resolvedProductId) return;
    const fetchShortReviews = async () => {
      setLoadingShortReviews(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/reviews/product/${resolvedProductId}`);
        setShortReviews(res.data);
      } catch {
        setShortReviews({ reviews: [], count: 0 });
      } finally {
        setLoadingShortReviews(false);
      }
    };
    fetchShortReviews();
  }, [resolvedProductId]);

  const handleReviewSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product?.id || !formName.trim() || formRating === 0) {
      setAlert({ type: "error", message: "Veuillez remplir tous les champs obligatoires." });
      return;
    }

    setIsSubmittingReview(true);
    try {
      await axios.post(`${API_BASE_URL}/reviews`, {
        productId: String(product.id),
        note: Number(formRating),
        nom: String(formName),
        etat: "encours",
        commentaire: formComment,
      });

      setAlert({ type: "success", message: "Votre avis a été envoyé pour validation !" });
      setReviewModalOpen(false);
      setFormName(""); setFormComment(""); setFormRating(0);
      setTimeout(() => setAlert(null), 5000);
    } catch (err: any) {
      setAlert({ type: "error", message: "Erreur lors de l'envoi." });
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <section className="bg-slate-50/50 py-16 border-t border-slate-100">
      <div className="max-w-6xl mx-auto px-6">

        {/* Toast Alert */}
        {alert && (
          <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[100] px-6 py-3 rounded-2xl shadow-2xl text-white flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-300 ${alert.type === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
            {alert.type === "success" ? <CheckCircle2 className="w-5 h-5" /> : <X className="w-5 h-5" />}
            <span className="font-medium">{alert.message}</span>
          </div>
        )}

        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="space-y-2">
            <h2 className="text-3xl font-bold text-slate-900 tracking-tight">Avis de la communauté</h2>
            <p className="text-slate-500 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-slate-400" />
              Basé sur {currentCount} retours clients
            </p>
          </div>
          <button
            onClick={() => setReviewModalOpen(true)}
            className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-3.5 rounded-xl font-semibold transition-all hover:shadow-lg active:scale-95"
          >
            Partager mon expérience
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">

          {/* Rating Summary Card */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
              <div className="text-center mb-8">
                <div className="text-6xl font-black text-slate-900 mb-2">{averageRating.toFixed(1)}</div>
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-6 h-6 ${i < Math.round(averageRating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200 fill-slate-200'}`} />
                  ))}
                </div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Note globale</p>
              </div>

              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((star) => {
                  const count = currentReviews.filter(r => Math.round(Number(r.note || r.rating)) === star).length;
                  const pct = currentCount > 0 ? (count / currentCount) * 100 : 0;
                  return (
                    <div key={star} className="flex items-center gap-4 text-sm">
                      <div className="w-8 font-semibold text-slate-600">{star} <Star className="inline w-3 h-3 mb-1" /></div>
                      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="w-8 text-right text-slate-400 font-medium">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-8 space-y-6">
            {loadingShortReviews ? (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-4">
                <Loader2 className="w-8 h-8 animate-spin" />
                <p>Récupération des avis...</p>
              </div>
            ) : currentReviews.length > 0 ? (
              currentReviews.slice(0, 6).map((r, i) => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:border-slate-200 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600 uppercase">
                        {(r.nom || r.name || '?')[0]}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-900 leading-tight">{r.nom || r.name || 'Client anonyme'}</h4>
                        <p className="text-xs text-slate-400 font-medium">{new Date(r.createdAt || r.created_at || Date.now()).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, starIndex) => (
                        <Star key={starIndex} className={`w-3.5 h-3.5 ${starIndex < Number(r.note || r.rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-slate-200 fill-slate-200'}`} />
                      ))}
                    </div>
                  </div>
                  <p className="text-slate-600 leading-relaxed text-[15px]">{r.commentaire || r.description || r.texte}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-300">
                <MessageSquare className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900">Aucun avis pour le moment</h3>
                <p className="text-slate-500 max-w-xs mx-auto mt-2">Soyez le premier à donner votre avis sur ce produit et aidez les autres acheteurs !</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal - Modernized */}
      {isReviewModalOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div onClick={() => setReviewModalOpen(false)} className="absolute inset-0 bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-300" />

          <div className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
            <button onClick={() => setReviewModalOpen(false)} className="absolute top-6 right-6 p-2 rounded-full hover:bg-slate-100 transition-colors">
              <X className="w-6 h-6 text-slate-400" />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-5 h-full">
              {/* Sidebar Modal */}
              <div className="md:col-span-2 bg-slate-50 p-8 border-r border-slate-100 flex flex-col items-center text-center">
                <div className="relative w-32 h-32 mb-6 rounded-2xl overflow-hidden shadow-lg border-4 border-white">
                  <Image src={product?.images?.[0] ? getImageUrl(product.images[0]) : "/logo.png.webp"} alt={product?.name || ""} fill className="object-cover" />
                </div>
                <h4 className="font-bold text-slate-900 line-clamp-2 mb-2">{product?.name}</h4>
                <div className="text-emerald-600 font-bold text-lg">
                  {product ? (product.promoPrice || product.price).toFixed(3) : "0.000"} DT
                </div>
              </div>

              {/* Form Content */}
              <div className="md:col-span-3 p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Votre avis compte</h3>

                <form onSubmit={handleReviewSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Note globale</label>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onMouseEnter={() => setFormHoverRating(star)}
                            onMouseLeave={() => setFormHoverRating(0)}
                            onClick={() => setFormRating(star)}
                            className="transition-transform active:scale-90"
                          >
                            <Star className={`w-8 h-8 ${star <= (formHoverRating || formRating) ? 'fill-amber-400 text-amber-400' : 'text-slate-200 fill-slate-200'}`} />
                          </button>
                        ))}
                      </div>
                      <span className="text-sm font-bold text-amber-600 ml-2">{ratingLabels[formHoverRating || formRating]}</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Nom complet</label>
                      <input
                        type="text"
                        required
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-900 focus:ring-0 transition-colors"
                        placeholder="Ex: Ahmed Ben Ali"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Message</label>
                      <textarea
                        required
                        rows={4}
                        value={formComment}
                        onChange={(e) => setFormComment(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-slate-900 focus:ring-0 transition-colors resize-none"
                        placeholder="Racontez-nous votre expérience..."
                      />
                    </div>
                  </div>

                  <button
                    disabled={isSubmittingReview}
                    className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold flex items-center justify-center gap-3 hover:bg-slate-800 disabled:opacity-50 transition-all shadow-xl shadow-slate-200"
                  >
                    {isSubmittingReview ? (
                      <> <Loader2 className="w-5 h-5 animate-spin" /> Envoi en cours... </>
                    ) : "Publier mon avis"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}